import { supabase } from './supabase';
import { logger } from './logger';

// ─── Types ───────────────────────────────────────────────────────────

export interface RefItem {
  id: string;
  code: string;
  nameFr: string;
  nameEn: string;
  nameAr: string;
  nameTr: string;
  nameEs: string;
  namePt: string;
  nameVi: string;
  active: boolean;
  sortOrder: number;
  translated: boolean;
  translationLocked: Record<string, boolean> | null;
  sectorId?: string;
  subsectorId?: string;
  positionId?: string;
  category?: string;
  skillGroupId?: string;
}

export const TRANSLATION_LANGS = [
  { key: 'en', label: 'Anglais' },
  { key: 'ar', label: 'Arabe' },
  { key: 'tr', label: 'Turc' },
  { key: 'es', label: 'Espagnol' },
  { key: 'pt', label: 'Portugais' },
  { key: 'vi', label: 'Vietnamien' },
] as const;

export interface SkillGroup {
  id: string;
  positionId: string;
  code: string;
  nameFr: string;
  nameEn?: string;
  sortOrder: number;
  active: boolean;
  skills?: RefItem[];
}

export interface SkillFamily {
  id: string;
  code: string;
  nameFr: string;
  nameEn?: string;
  description?: string;
  active: boolean;
  memberCount?: number;
  members?: RefItem[];
}

export interface SkillUsageStat {
  skillId: string;
  skillName: string;
  checkedCount: number;
  uncheckedCount: number;
  ratio: number;
  lastUpdated: string;
}

export interface SkillProximityPair {
  id: string;
  skillA: { id: string; nameFr: string; code: string };
  skillB: { id: string; nameFr: string; code: string };
  similarity: number;
  status: 'pending' | 'confirmed' | 'merged' | 'dismissed';
  reviewedAt?: string;
}

export type RefTable = 'sectors' | 'subsectors' | 'positions' | 'skills' | 'skill_groups' | 'skill_families' | 'certifications';

// ─── Mappers ─────────────────────────────────────────────────────────

function mapRowToRefItem(r: any): RefItem {
  return {
    id: r.id,
    code: r.code,
    nameFr: r.name_fr || '',
    nameEn: r.name_en || '',
    nameAr: r.name_ar || '',
    nameTr: r.name_tr || '',
    nameEs: r.name_es || '',
    namePt: r.name_pt || '',
    nameVi: r.name_vi || '',
    active: r.active ?? true,
    sortOrder: r.sort_order || 0,
    translated: !!(r.name_en && r.name_en.trim()),
    translationLocked: r.translation_locked || null,
    sectorId: r.sector_id,
    subsectorId: r.subsector_id,
    positionId: r.position_id,
    category: r.category,
    skillGroupId: r.skill_group_id,
  };
}

function mapRefItemToRow(item: Partial<Omit<RefItem, 'id'>>): Record<string, any> {
  const row: Record<string, any> = {};
  if (item.code !== undefined) row.code = item.code;
  if (item.nameFr !== undefined) row.name_fr = item.nameFr;
  if (item.nameEn !== undefined) row.name_en = item.nameEn;
  if (item.nameAr !== undefined) row.name_ar = item.nameAr;
  if (item.active !== undefined) row.active = item.active;
  if (item.sortOrder !== undefined) row.sort_order = item.sortOrder;
  if (item.sectorId !== undefined) row.sector_id = item.sectorId || null;
  if (item.subsectorId !== undefined) row.subsector_id = item.subsectorId || null;
  if (item.positionId !== undefined) row.position_id = item.positionId || null;
  if (item.category !== undefined) row.category = item.category || null;
  if (item.skillGroupId !== undefined) row.skill_group_id = item.skillGroupId || null;
  return row;
}

// ─── CRUD Generic ────────────────────────────────────────────────────

export async function fetchReferential(table: RefTable): Promise<RefItem[]> {
  let query = supabase.from(table).select('*');

  // Only order by sort_order if the table has that column
  const tablesWithSortOrder: RefTable[] = ['positions', 'skills', 'skill_groups', 'certifications'];
  if (tablesWithSortOrder.includes(table)) {
    query = query.order('sort_order', { ascending: true });
  } else {
    query = query.order('name_fr', { ascending: true });
  }

  const { data, error } = await query;

  if (error) {
    logger.error('fetch_referential', 'referentialService', { table, error: error.message });
    throw error;
  }
  logger.technical('fetch_referential_ok', 'referentialService', { table, count: (data || []).length });
  return (data || []).map(mapRowToRefItem);
}

export async function createReferentialItem(
  table: RefTable,
  item: Omit<RefItem, 'id'>
): Promise<RefItem> {
  const row = mapRefItemToRow(item);
  logger.functional('create_referential_item', 'referentialService', { table, code: item.code });

  const { data, error } = await supabase
    .from(table)
    .insert(row)
    .select()
    .single();

  if (error) {
    logger.error('create_referential_item', 'referentialService', { table, error: error.message });
    throw error;
  }
  logger.technical('create_referential_item_ok', 'referentialService', { table, id: data.id });
  triggerTranslation(table as TranslatableTable, [data.id]);
  return mapRowToRefItem(data);
}

export async function updateReferentialItem(
  table: RefTable,
  id: string,
  item: Partial<Omit<RefItem, 'id'>>
): Promise<void> {
  const row = mapRefItemToRow(item);
  logger.functional('update_referential_item', 'referentialService', { table, id });

  const { error } = await supabase
    .from(table)
    .update(row)
    .eq('id', id);

  if (error) {
    logger.error('update_referential_item', 'referentialService', { table, id, error: error.message });
    throw error;
  }
  logger.technical('update_referential_item_ok', 'referentialService', { table, id });
  if (item.nameFr !== undefined) {
    triggerTranslation(table as TranslatableTable, [id]);
  }
}

export async function deleteReferentialItem(table: RefTable, id: string): Promise<void> {
  logger.functional('delete_referential_item', 'referentialService', { table, id });
  const { error } = await supabase
    .from(table)
    .delete()
    .eq('id', id);

  if (error) {
    logger.error('delete_referential_item', 'referentialService', { table, id, error: error.message });
    throw error;
  }
  logger.technical('delete_referential_item_ok', 'referentialService', { table, id });
}

export async function toggleReferentialActive(table: RefTable, id: string, active: boolean): Promise<void> {
  logger.functional('toggle_referential_active', 'referentialService', { table, id, active });
  const { error } = await supabase
    .from(table)
    .update({ active })
    .eq('id', id);

  if (error) {
    logger.error('toggle_referential_active', 'referentialService', { table, id, error: error.message });
    throw error;
  }
}

// ─── Skill Groups ────────────────────────────────────────────────────

export async function fetchSkillGroups(positionId?: string): Promise<SkillGroup[]> {
  let query = supabase
    .from('skill_groups')
    .select('*')
    .order('sort_order', { ascending: true });

  if (positionId) query = query.eq('position_id', positionId);

  const { data, error } = await query;
  if (error) throw error;

  return (data || []).map((r: any) => ({
    id: r.id,
    positionId: r.position_id,
    code: r.code,
    nameFr: r.name_fr,
    nameEn: r.name_en,
    sortOrder: r.sort_order || 0,
    active: r.active ?? true,
  }));
}

export async function fetchSkillsByGroup(groupId: string): Promise<RefItem[]> {
  const { data, error } = await supabase
    .from('skills')
    .select('*')
    .eq('skill_group_id', groupId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapRowToRefItem);
}

// ─── Skill Families ──────────────────────────────────────────────────

export async function fetchSkillFamilies(): Promise<SkillFamily[]> {
  const { data, error } = await supabase
    .from('skill_families')
    .select('*, skill_family_members(skill_id)')
    .order('name_fr', { ascending: true });

  if (error) throw error;

  return (data || []).map((r: any) => ({
    id: r.id,
    code: r.code,
    nameFr: r.name_fr,
    nameEn: r.name_en,
    description: r.description,
    active: r.active ?? true,
    memberCount: (r.skill_family_members || []).length,
  }));
}

export async function fetchFamilyMembers(familyId: string): Promise<RefItem[]> {
  const { data, error } = await supabase
    .from('skill_family_members')
    .select('skill_id, skills(*)')
    .eq('family_id', familyId);

  if (error) throw error;
  return (data || []).map((r: any) => mapRowToRefItem(r.skills));
}

export async function addSkillToFamily(skillId: string, familyId: string): Promise<void> {
  const { error } = await supabase
    .from('skill_family_members')
    .insert({ skill_id: skillId, family_id: familyId });

  if (error) throw error;
  logger.functional('add_skill_to_family', 'referentialService', { skillId, familyId });
}

export async function removeSkillFromFamily(skillId: string, familyId: string): Promise<void> {
  const { error } = await supabase
    .from('skill_family_members')
    .delete()
    .eq('skill_id', skillId)
    .eq('family_id', familyId);

  if (error) throw error;
  logger.functional('remove_skill_from_family', 'referentialService', { skillId, familyId });
}

export async function createSkillFamily(family: { code: string; nameFr: string; nameEn?: string; description?: string }): Promise<SkillFamily> {
  const { data, error } = await supabase
    .from('skill_families')
    .insert({
      code: family.code,
      name_fr: family.nameFr,
      name_en: family.nameEn || null,
      description: family.description || null,
    })
    .select()
    .single();

  if (error) throw error;
  return {
    id: data.id,
    code: data.code,
    nameFr: data.name_fr,
    nameEn: data.name_en,
    description: data.description,
    active: data.active,
    memberCount: 0,
  };
}

export async function updateSkillFamily(id: string, updates: { nameFr?: string; nameEn?: string; description?: string; active?: boolean }): Promise<void> {
  const row: Record<string, any> = {};
  if (updates.nameFr !== undefined) row.name_fr = updates.nameFr;
  if (updates.nameEn !== undefined) row.name_en = updates.nameEn;
  if (updates.description !== undefined) row.description = updates.description;
  if (updates.active !== undefined) row.active = updates.active;

  const { error } = await supabase
    .from('skill_families')
    .update(row)
    .eq('id', id);

  if (error) throw error;
}

// ─── Skill Usage Stats ───────────────────────────────────────────────

export async function fetchSkillUsageStats(limit = 50): Promise<SkillUsageStat[]> {
  const { data, error } = await supabase
    .from('skill_usage_stats')
    .select('*, skills(name_fr)')
    .order('checked_count', { ascending: false })
    .limit(limit);

  if (error) throw error;

  return (data || []).map((r: any) => ({
    skillId: r.skill_id,
    skillName: r.skills?.name_fr || '',
    checkedCount: r.checked_count || 0,
    uncheckedCount: r.unchecked_count || 0,
    ratio: r.checked_count > 0 ? r.checked_count / (r.checked_count + r.unchecked_count) : 0,
    lastUpdated: r.last_updated,
  }));
}

// ─── Skill Proximity Pairs ──────────────────────────────────────────

export async function fetchProximityPairs(status?: string): Promise<SkillProximityPair[]> {
  let query = supabase
    .from('skill_proximity_pairs')
    .select(`
      id, similarity, status, reviewed_at,
      skill_a:skills!skill_proximity_pairs_skill_a_id_fkey(id, name_fr, code),
      skill_b:skills!skill_proximity_pairs_skill_b_id_fkey(id, name_fr, code)
    `)
    .order('similarity', { ascending: false });

  if (status) query = query.eq('status', status);

  const { data, error } = await query;
  if (error) throw error;

  return (data || []).map((r: any) => ({
    id: r.id,
    skillA: { id: r.skill_a.id, nameFr: r.skill_a.name_fr, code: r.skill_a.code },
    skillB: { id: r.skill_b.id, nameFr: r.skill_b.name_fr, code: r.skill_b.code },
    similarity: r.similarity,
    status: r.status,
    reviewedAt: r.reviewed_at,
  }));
}

export async function updateProximityPairStatus(
  id: string,
  status: 'confirmed' | 'merged' | 'dismissed'
): Promise<void> {
  const { error } = await supabase
    .from('skill_proximity_pairs')
    .update({
      status,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', id);

  if (error) throw error;
  logger.functional('update_proximity_status', 'referentialService', { id, status });
}

// ─── Translation ─────────────────────────────────────────────────────

type TranslatableTable = 'sectors' | 'subsectors' | 'positions' | 'skills' | 'skill_groups';

export async function updateTranslation(
  table: RefTable,
  id: string,
  lang: string,
  value: string,
  lock: boolean = true,
): Promise<void> {
  const updateData: Record<string, any> = { [`name_${lang}`]: value };

  // If locking, update the translation_locked JSONB
  if (lock) {
    const { data: current } = await supabase.from(table).select('translation_locked').eq('id', id).single();
    const locked = current?.translation_locked || {};
    locked[lang] = true;
    updateData.translation_locked = locked;
  }

  const { error } = await supabase.from(table).update(updateData).eq('id', id);
  if (error) {
    logger.error('update_translation', 'referentialService', { table, id, lang, error: error.message });
    throw error;
  }
}

export async function toggleTranslationLock(
  table: RefTable,
  id: string,
  lang: string,
  locked: boolean,
): Promise<Record<string, boolean>> {
  const { data: current } = await supabase.from(table).select('translation_locked').eq('id', id).single();
  const lockState = current?.translation_locked || {};
  lockState[lang] = locked;
  if (!locked) delete lockState[lang];

  const { error } = await supabase.from(table).update({ translation_locked: Object.keys(lockState).length > 0 ? lockState : null }).eq('id', id);
  if (error) throw error;
  return lockState;
}

export async function triggerTranslation(table: TranslatableTable, ids: string[]): Promise<{ translated: number; total: number } | null> {
  if (ids.length === 0) return null;
  try {
    const { data, error } = await supabase.functions.invoke('translate-referential', {
      body: { table, ids },
    });
    if (error) {
      logger.error('trigger_translation', 'referentialService', { table, ids, error: String(error) });
      return null;
    }
    logger.technical('trigger_translation_ok', 'referentialService', { table, count: ids.length, result: data });
    return data as { translated: number; total: number };
  } catch (err) {
    logger.error('trigger_translation', 'referentialService', { table, error: String(err) });
    return null;
  }
}

// ─── Quick Create (inline add) ──────────────────────────────────────

function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

export async function quickCreateSkill(
  nameFr: string,
  groupId: string,
  positionId: string,
  sectorId: string,
  positionCode?: string
): Promise<RefItem> {
  const prefix = positionCode || 'skill';
  const code = `${slugify(prefix)}_${slugify(nameFr)}`;

  const { data, error } = await supabase
    .from('skills')
    .insert({
      code,
      name_fr: nameFr.trim(),
      skill_group_id: groupId,
      position_id: positionId,
      sector_id: sectorId,
      active: true,
      sort_order: 0,
    })
    .select()
    .single();

  if (error) throw error;
  logger.functional('quick_create_skill', 'referentialService', { code, nameFr });
  triggerTranslation('skills', [data.id]);
  return mapRowToRefItem(data);
}

export async function quickCreateGroup(
  nameFr: string,
  positionId: string,
  positionCode?: string
): Promise<RefItem> {
  const prefix = positionCode || 'grp';
  const code = `${slugify(prefix)}_g_${slugify(nameFr)}`;

  const { data, error } = await supabase
    .from('skill_groups')
    .insert({
      code,
      name_fr: nameFr.trim(),
      position_id: positionId,
      active: true,
      sort_order: 0,
    })
    .select()
    .single();

  if (error) throw error;
  logger.functional('quick_create_group', 'referentialService', { code, nameFr });
  triggerTranslation('skill_groups', [data.id]);
  return mapRowToRefItem(data);
}

export async function quickCreatePosition(
  nameFr: string,
  sectorId: string,
  subsectorId?: string
): Promise<RefItem> {
  const code = `pos_${slugify(nameFr)}`;

  const { data, error } = await supabase
    .from('positions')
    .insert({
      code,
      name_fr: nameFr.trim(),
      sector_id: sectorId,
      subsector_id: subsectorId || null,
      active: true,
      sort_order: 0,
    })
    .select()
    .single();

  if (error) throw error;
  logger.functional('quick_create_position', 'referentialService', { code, nameFr });
  triggerTranslation('positions', [data.id]);
  return mapRowToRefItem(data);
}

// ─── Helpers ─────────────────────────────────────────────────────────

export async function fetchPositionIdByCode(code: string): Promise<string | null> {
  const { data } = await supabase
    .from('positions')
    .select('id')
    .eq('code', code)
    .single();
  return data?.id || null;
}

export async function fetchAllSkills(): Promise<RefItem[]> {
  const { data, error } = await supabase
    .from('skills')
    .select('*')
    .eq('active', true)
    .order('name_fr', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapRowToRefItem);
}
